/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useRef, useState, MouseEvent } from 'react';
import { Box, Checkbox, IconButton, Menu, MenuItem } from '@mui/material';
import Close from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useReducerAtom } from 'jotai/utils';
import { css } from '@emotion/react';
import {
  chatListAtom,
  toggleSelectedChatIdsAtom,
  selectedChatIdsListAtom,
  isAllChatsSelectedAtom,
  excludedChatIdsAtom,
  chatFilterStatusCountsAtom,
} from '../../../../states/chat-list';
import { chatIdsAtom } from '../../../../states/chat';
import { chatListReducer } from '../../../../states/chat-list-reducer';
import { ChatListReducerActionTypes, ChatStatusOptions } from '../../../../typings';
import { resolveFilterType, getTotalFilterCount, getRawTotalCountString } from '../../../../utils/chat-filter-count';
import { useAtom, useAtomValue } from 'jotai';
import { getCurrentUserId } from '../../../../../../../infra/firebase/init';
import { QSTypography } from '../../../../../../../shared/components/atoms';
import { useSelectedOrganization } from '../../../../../../../shared/hooks/use-organizations';
import { TextEllipsisStyles } from '../../../../../../../shared/components/emotion';
import { teamMembersAtom } from '../../../../states/team-members';
import { isEmbedAtom } from '../../../../../../embed/state';
import useEmbedVisibilitySettings from '../../../../../../embed/hooks/use-embed-visibility-settings';
import Loader from '../../../../../../../shared/components/molecules/loader';
import SearchIcon from '../../../../../../../shared/icons/icons-revamp/search';
import TickSquareIcon from '../../../../../../../shared/icons/icons-revamp/tick_square';
import BulkActionIcons from '../bulk-action-icons';
import QSTooltip from '../../../../../../../shared/components/molecules/tooltip';

const ChatHeaderMain = ({ showLoading }: { showLoading?: boolean }) => {
  const currentUserId = getCurrentUserId();
  const { name } = useSelectedOrganization() || {};
  const { data: teamMembers = [] } = useAtomValue(teamMembersAtom);

  const isEmbed = useAtomValue(isEmbedAtom);
  const { showChatSearch, showChatSelection } = useEmbedVisibilitySettings();
  const [toggleSelectedChatIds, setToggleSelectedChatIds] = useAtom(toggleSelectedChatIdsAtom);
  const [selectedChatIdsList, setSelectedChatIdsList] = useAtom(selectedChatIdsListAtom);
  const [isAllChatsSelected, setIsAllChatsSelected] = useAtom(isAllChatsSelectedAtom);
  const [excludedChatIds, setExcludedChatIds] = useAtom(excludedChatIdsAtom);
  const { data: loadedChatIds = [] } = useAtomValue(chatIdsAtom);
  const { data: filterCounts } = useAtomValue(chatFilterStatusCountsAtom);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [
    {
      assignedUserId,
      status: selectedChatStatus,
      slaStatus: selectedSlaStatus,
      tags: selectedTags,
      tagIds: selectedTagIds,
      waba: selectedWabas,
      searchTerm,
      customList,
      currentCustomListId,
      conversationView,
    },
    dispatch,
  ] = useReducerAtom(chatListAtom, chatListReducer);

  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setIsAllChatsSelected(false);
    setExcludedChatIds([]);
    setSelectedChatIdsList([]);
  }, [
    assignedUserId,
    selectedChatStatus,
    selectedSlaStatus,
    selectedTags,
    selectedTagIds,
    selectedWabas,
    searchTerm,
    customList,
    currentCustomListId,
    conversationView,
    setIsAllChatsSelected,
    setExcludedChatIds,
    setSelectedChatIdsList,
  ]);

  const getChatHeaderName = useCallback(() => {
    let chatHeaderName = name ?? '';
    const status = ChatStatusOptions.find(({ value }) => value === selectedChatStatus);
    if (selectedSlaStatus === 'breached') {
      chatHeaderName = `SLA breached${status ? ` - ${status.name}` : ''}`;
    } else if (assignedUserId === 'null') {
      chatHeaderName = `Unassigned${status ? ` - ${status.name}` : ''}`;
    } else if (typeof assignedUserId !== 'string' || assignedUserId.length === 0) {
      chatHeaderName = `All chats${status ? ` - ${status.name}` : ''}`;
    } else if (typeof currentUserId === 'string' && currentUserId.length > 0 && currentUserId === assignedUserId) {
      chatHeaderName = `My chats${status ? ` - ${status.name}` : ''}`;
    } else if (typeof assignedUserId === 'string' && assignedUserId.length > 0 && assignedUserId !== 'null') {
      const assignedUser = teamMembers?.find?.((member) => member.id === assignedUserId);
      if (assignedUser) {
        chatHeaderName = `Assigned to ${assignedUser?.name}`;
      }
    } else if ((selectedTags ?? []).length > 0) {
      if (selectedTags.length > 2) {
        chatHeaderName = `${selectedTags[0]} and ${selectedTags.length - 1} other`;
      } else {
        chatHeaderName = selectedTags.join(' and ');
      }
    } else if ((selectedWabas ?? []).length > 0) {
      if (selectedWabas.length > 2) {
        chatHeaderName = `${selectedWabas[0]} and ${selectedWabas.length - 1} other`;
      } else {
        chatHeaderName = selectedWabas.join(' and ');
      }
    }
    return chatHeaderName;
  }, [name, assignedUserId, selectedChatStatus, selectedSlaStatus]);

  const hasManualSelection = selectedChatIdsList.length > 0;
  const someSelected = hasManualSelection && !isAllChatsSelected;
  const hasExclusions = isAllChatsSelected && excludedChatIds.length > 0;

  const handleSelectAllToggle = useCallback(() => {
    if (isAllChatsSelected && !hasExclusions) {
      setIsAllChatsSelected(false);
      setExcludedChatIds([]);
      setSelectedChatIdsList([]);
    } else {
      setIsAllChatsSelected(true);
      setExcludedChatIds([]);
      setSelectedChatIdsList([...loadedChatIds]);
    }
  }, [
    isAllChatsSelected,
    hasExclusions,
    loadedChatIds,
    setIsAllChatsSelected,
    setExcludedChatIds,
    setSelectedChatIdsList,
  ]);

  const handleExitSelectionMode = useCallback(() => {
    setSelectedChatIdsList([]);
    setToggleSelectedChatIds(false);
    setIsAllChatsSelected(false);
    setExcludedChatIds([]);
  }, [setSelectedChatIdsList, setToggleSelectedChatIds, setIsAllChatsSelected, setExcludedChatIds]);

  const handleEnterSelectionMode = useCallback(() => {
    setMenuAnchorEl(null);
    setToggleSelectedChatIds(true);
  }, [setToggleSelectedChatIds]);

  const getTotalCount = useCallback(() => {
    const filterType = resolveFilterType({
      slaStatus: selectedSlaStatus,
      assignedUserId,
      currentUserId,
    });
    return getTotalFilterCount({ filterCounts, filterType, status: selectedChatStatus });
  }, [filterCounts, selectedSlaStatus, assignedUserId, currentUserId, selectedChatStatus]);

  const getRawTotalCount = useCallback(() => {
    const filterType = resolveFilterType({
      slaStatus: selectedSlaStatus,
      assignedUserId,
      currentUserId,
    });
    return getRawTotalCountString({ filterCounts, filterType, status: selectedChatStatus });
  }, [filterCounts, selectedSlaStatus, assignedUserId, currentUserId, selectedChatStatus]);

  const getSelectedCountText = useCallback(() => {
    // In search mode the filter total reflects the full filter, not search results — hide it
    if (searchTerm) {
      return selectedChatIdsList.length > 0 ? `${selectedChatIdsList.length} selected` : 'Select chats';
    }

    const totalCount = getTotalCount();
    const rawTotal = getRawTotalCount();

    // While APIs are loading totalCount can be 0/null — avoid showing "0/0 selected"
    if ((isAllChatsSelected || selectedChatIdsList.length > 0) && !totalCount && !rawTotal) {
      return 'Select chats';
    }

    // Abbreviated totals like "1K+" can't be parsed accurately with parseInt.
    // Pin to rawTotal/rawTotal regardless of exclusions so we never show "0/1K+".
    const isAbbreviated = rawTotal != null && isNaN(Number(rawTotal));
    if (isAllChatsSelected && isAbbreviated) {
      return `${rawTotal}/${rawTotal} selected`;
    }

    const selectedCount = isAllChatsSelected
      ? Math.max(0, (totalCount ?? 0) - excludedChatIds.length)
      : selectedChatIdsList.length;

    if (selectedCount > 0 || isAllChatsSelected) {
      const totalDisplay = rawTotal ?? totalCount;
      return totalDisplay != null
        ? `${selectedCount > (totalCount ?? 0) ? totalDisplay : selectedCount}/${totalDisplay} selected`
        : `${selectedCount} selected`;
    }
    return 'Select chats';
  }, [
    isAllChatsSelected,
    excludedChatIds.length,
    selectedChatIdsList.length,
    getTotalCount,
    getRawTotalCount,
    searchTerm,
  ]);

  const renderHeaderIcons = () => {
    if (showLoading) {
      return <Loader size={16} />;
    }
    if (showChatSearch) {
      return (
        <>
          {(!isEmbed || showChatSearch) && (
            <QSTooltip title="Search" placement="top">
              <IconButton
                css={css`
                  padding: 8px;
                `}
                onClick={() =>
                  dispatch({
                    type: ChatListReducerActionTypes.SEARCH_STATUS,
                    data: true,
                  })
                }
              >
                <SearchIcon color="#2d2d2d" />
              </IconButton>
            </QSTooltip>
          )}

          <BulkActionIcons />

          {(!isEmbed || showChatSelection) && !toggleSelectedChatIds && (
            <>
              <QSTooltip title="More options" placement="top">
                <IconButton
                  onClick={(e: MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(e.currentTarget)}
                  css={css`
                    padding: 8px;
                  `}
                >
                  <MoreVertIcon sx={{ fontSize: 20, color: '#2d2d2d' }} />
                </IconButton>
              </QSTooltip>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                css={css`
                  & .MuiMenu-paper {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px 0 rgba(0, 0, 0, 0.05);
                  }
                  & .MuiMenu-list {
                    border-radius: 4px;
                    border: 1px solid rgba(30, 41, 59, 0.14);
                    padding: 0;
                  }
                `}
              >
                <MenuItem
                  onClick={handleEnterSelectionMode}
                  css={css`
                    display: flex;
                    height: 40px;
                    padding: 6px 12px;
                    align-items: center;
                    gap: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #2d2d2d;
                    &:hover {
                      background-color: #f5f5f5;
                    }
                  `}
                >
                  <TickSquareIcon />
                  Select chats
                </MenuItem>
              </Menu>
            </>
          )}

          {toggleSelectedChatIds && (
            <QSTooltip title="Exit selection" placement="top">
              <IconButton
                onClick={handleExitSelectionMode}
                css={css`
                  padding: 8px;
                `}
              >
                <Close sx={{ fontSize: 20, color: '#2d2d2d' }} />
              </IconButton>
            </QSTooltip>
          )}
        </>
      );
    }
  };

  return (
    <Box
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 100%;
      `}
    >
      <Box
        css={css`
          display: flex;
          justify-content: flex-start;
          align-items: center;
          height: 100%;
          column-gap: 8px;
          overflow: hidden;
        `}
      >
        {toggleSelectedChatIds && (
          <>
            <QSTooltip title={isAllChatsSelected && !hasExclusions ? 'Deselect all' : 'Select all'} placement="top">
              <Checkbox
                checked={isAllChatsSelected && !hasExclusions}
                indeterminate={someSelected || hasExclusions}
                onChange={handleSelectAllToggle}
                disabled={loadedChatIds.length === 0}
                sx={{
                  padding: 0,
                  flexShrink: 0,
                  color: '#7a7a7a',
                  '&.Mui-checked': {
                    color: '#3f906d',
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: '#3f906d',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 20,
                  },
                }}
              />
            </QSTooltip>
            <QSTypography
              css={css(
                css`
                  font-weight: 600;
                  font-size: 16px;
                  line-height: 125%;
                  color: #2d2d2d;
                `,
                TextEllipsisStyles
              )}
            >
              {getSelectedCountText()}
            </QSTypography>
          </>
        )}
        {!toggleSelectedChatIds && (
          <QSTypography
            css={css(
              css`
                font-weight: 600;
                font-size: 16px;
                line-height: 125%;
              `,
              TextEllipsisStyles
            )}
          >
            {getChatHeaderName()}
          </QSTypography>
        )}
      </Box>

      <Box
        css={css`
          display: flex;
          justify-content: flex-end;
          align-items: center;
          column-gap: 0px;
        `}
      >
        {renderHeaderIcons()}
      </Box>
    </Box>
  );
};

export default ChatHeaderMain;
